import { useQuery } from "@apollo/client";
import { useIsAuthenticated } from "@azure/msal-react";
import { useCallback } from "react";
import { FETCH_USER as fetchUser } from "../graphql/query/User";
import { hasE2eSession } from "./e2eSession";

export const USER_ROLES = {
  ADMIN: 1,
  SUPER_ADMIN: 2,
  DEFAULT: 3,
  SALES: 4,
  CONTACTS: 5,
  CURATORIAL: 6,
  INVENTORY: 7,
  DIGITAL_AND_AUDIENCES: 8,
  BASE_READ: 9,
  EMPLOYEE: 10,
  WHITECUBE_GROUP: 11,
};

const useUser = props => {
  const { skip = false, withRoles = false } = props || {};
  const isAuthenticated = useIsAuthenticated() || hasE2eSession();

  // Get token from localStorage (set by useMSAL)
  const token = localStorage.getItem("user:token");
  const shouldSkip = skip || !isAuthenticated || !token;

  const { data, error, loading } = useQuery(
    fetchUser({ permissions: {}, roles: withRoles ? {} : false }),
    { skip: shouldSkip },
  );

  const user = data?.user;
  const hasPermission = useCallback(
    (permissions = []) => {
      const checkPermissions = Array.isArray(permissions)
        ? permissions
        : (typeof permissions === "string" && [permissions]) || [];

      // bypass permission checker if there's no items to check
      if (!checkPermissions.length) return true;

      const userPermissions = user?.permissions || {};
      const flatUserPermissions = Object.values(userPermissions).reduce(
        (acc, item) => {
          const items = [];
          Object.keys(item).forEach(key => {
            if (item[key]) {
              items.push(key);
            }
          });
          return [...acc, ...items];
        },
        [],
      );
      let count = 0;
      checkPermissions?.forEach(permission => {
        if (flatUserPermissions.includes(permission)) {
          count += 1;
        }
      });
      if (count === checkPermissions?.length) {
        return true;
      } else {
        return false;
      }
    },
    [user],
  );

  const hasRole = useCallback(
    roleId => {
      const roles = user?.roles || [];

      return roles.some(role => role.id === roleId);
    },
    [user],
  );

  const canUseCompany = useCallback(
    company => {
      const companyPermissions = user?.permissions?.COMPANY || {};
      const applyCompanyIds = companyPermissions?.COMPANY_APPLY_IDS;
      const allowedCompanyIds = Object.entries(
        companyPermissions?.COMPANY_IDS || {},
      )
        .filter(([, value]) => value === true)
        .map(([key]) => Number(key?.split?.("_")?.[1]))
        .filter(Number.isFinite);

      if (company?.active !== -1) {
        return false;
      }

      if (applyCompanyIds === false) {
        return allowedCompanyIds.includes(company?.id);
      }

      return true;
    },
    [user],
  );

  return {
    error,
    hasPermission,
    loading,
    user,
    canUseCompany,
    hasRole,
  };
};

export default useUser;
